import React, { useEffect, useRef } from "react";
import "./ticker.css";



export default function Ticker({ items }) {
  const contentRef = useRef(null);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const velocity = useRef(0);
  const lastX = useRef(0);
  const lastTime = useRef(0);
  const momentumId = useRef(null);
  
 
  // -----------------------------
  // START DRAG
  // -----------------------------
  const startDrag = (clientX) => {
    const el = contentRef.current;
    if (!el) return;

    isDragging.current = true;
    startX.current = clientX;

    const matrix = new DOMMatrixReadOnly(
      getComputedStyle(el).transform
    );

    currentX.current = matrix.m41;
    lastX.current = clientX;
    lastTime.current = Date.now();

    velocity.current = 0;

    // STOP animation
    el.style.animation = "none";
  };

  // -----------------------------
  // MOVE
  // -----------------------------
  const onMove = (clientX) => {
    if (!isDragging.current) return;

    const dx = clientX - startX.current;
    const newX = currentX.current + dx;

    const now = Date.now();
    const dt = now - lastTime.current;

    if (dt > 0) {
      velocity.current = (clientX - lastX.current) / dt;
      lastX.current = clientX;
      lastTime.current = now;
    }

    contentRef.current.style.transform = `translateX(${newX}px)`;
  };

  // -----------------------------
  // END DRAG → INERTIA
  // -----------------------------
  const endDrag = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
  
    let v = velocity.current * 1000; // px/sec
    let pos = getTranslateX();
  
    const friction = 0.95;
  
    const step = () => {
      v *= friction;
      pos += v * 0.016; // ~60fps
  
      contentRef.current.style.transform = `translateX(${pos}px)`;
  
      if (Math.abs(v) > 5) {
        momentumId.current = requestAnimationFrame(step);
      } else {
        // ✅ THIS is where it goes
        resumeAnimation(pos);
      }
    };
  
    momentumId.current = requestAnimationFrame(step);
  };

  // -----------------------------
  // HELPERS
  // -----------------------------
  const getTranslateX = () => {
    const style = getComputedStyle(contentRef.current);
    const matrix = new DOMMatrixReadOnly(style.transform);
    return matrix.m41;
  };

 

  const resumeAnimation = (currentPos) => {
    const el = contentRef.current;
    if (!el) return;
  
    const width = el.scrollWidth / 2;
  
    const duration = parseFloat(
      getComputedStyle(el).getPropertyValue("--ticker-speed")
    );
  
    const pxPerSecond = width / duration;
  
    // ✅ FIXED direction
    let normalized = currentPos % width;
    if (normalized > 0) normalized -= width;
  
    const elapsed = Math.abs(normalized) / pxPerSecond;
  
// 1️⃣ remove animation first
el.style.animation = "none";

// 2️⃣ apply exact position
el.style.transform = `translateX(${normalized}px)`;

// 3️⃣ force browser to APPLY it
void el.offsetWidth;

// 4️⃣ clear transform so animation takes over cleanly
el.style.transform = "";

// 5️⃣ restart animation from correct time
//el.style.animation = `tickerMove ${duration}s linear infinite`;
el.style.animation = "";
el.style.animationDelay = `-${elapsed}s`;

};


// -----------------------------
// FREEZE ON HOVERED ITEM (FIXED)
// -----------------------------
const freezeOnItem = (index) => {
    const el = contentRef.current;
    if (!el) return;
  
    // stop animation
    el.style.animation = "none";
  
    const item = el.children[index];
    if (!item) return;
  
    // REAL position inside ticker-content (ignores animation transform)
    const itemX = item.offsetLeft + item.offsetWidth / 2;
  
    // center of container
    const containerWidth = el.parentElement.offsetWidth;
    const centerX = containerWidth / 2;
  
    // how much we need to shift
    const offset = centerX - itemX;
  
    // apply transform
    el.style.transform = `translateX(${offset}px)`;
  };
  
  // -----------------------------
  const resumeFromFreeze = () => {
    const el = contentRef.current;
    if (!el) return;
  
    el.style.transform = "";
    el.style.animation = "";
  };
  

  // -----------------------------
  // EVENTS
  // -----------------------------
  useEffect(() => {
    const handleMouseMove = (e) => onMove(e.pageX);
    const handleMouseUp = () => endDrag();

    const handleTouchMove = (e) => onMove(e.touches[0].clientX);
    const handleTouchEnd = () => endDrag();

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", handleTouchEnd);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);

      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  // -----------------------------
  // SPEED (UNCHANGED LOGIC)
  // -----------------------------
  useEffect(() => {
    if (!contentRef.current) return;

    const width = contentRef.current.scrollWidth;
    const pxPerSecond = 100;
    const duration = width / pxPerSecond;
    contentRef.current.style.setProperty("--ticker-speed-base", `${duration}s`);
    contentRef.current.style.setProperty(
      "--ticker-speed",
      `${duration}s`
    );
  }, [items]);

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div className="ticker-container">
      <div
        className="ticker-content"
        ref={contentRef}
        style={{ cursor: "grab" }}
        onMouseDown={(e) => startDrag(e.pageX)}
        onTouchStart={(e) => startDrag(e.touches[0].clientX)}
      >
        {[...items, ...items].map((item, i) => {
          const price = Number(item.price) || 0;
          const diff = Number(item.diff) || 0;
          const pct = Number(item.pct) || 0;

          const isUp = diff > 0;
          const isDown = diff < 0;

          const color = isUp
            ? "#00ff00"
            : isDown
            ? "#ff4444"
            : "#ffffff";

          const arrow = isUp ? "▲" : isDown ? "▼" : "•";

          return (
            <div
              key={i}
              className="ticker-item"
              onMouseEnter={() => freezeOnItem(i)}
              onMouseLeave={resumeFromFreeze}
              style={{
                display: "inline-block",
                marginRight: "25px",
              }}
            >
              <span className="ticker-symbol" style={{ color }}>
                {item.symbol}
              </span>

              <span className="ticker-price" style={{ color }}>
                {" "}({price.toFixed(2)})
              </span>

              <span
                className="ticker-arrow"
                style={{ color, marginLeft: "4px" }}
              >
                {arrow}
              </span>

              <span className="ticker-change" style={{ color }}>
                {" "}
                {diff > 0 ? "+" : ""}
                {diff.toFixed(2)} | {pct.toFixed(2)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}